import React, { useState, useRef, useEffect } from 'react';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { useDoorCrewCard } from './DoorCrewCardContext';
import DropoffAssignedChip from './DropoffAssignedChip';
import SeatActions from './SeatActions';
import { DoorCrewSeat, SeatRole } from './types';

const NONE_ROLE_VALUE = '';

const compactControlSx = {
  '& .MuiInputBase-root': { fontSize: '13px' },
  '& .MuiInputLabel-root': { fontSize: '12px' },
};

interface CanvasserSectionProps {
  canvasserId: number;
  seat: DoorCrewSeat;
  onDeleteSeat: () => void;
}

export default function CanvasserSection({ canvasserId, seat, onDeleteSeat }: CanvasserSectionProps) {
  const { canvassers, onUpdateSeat } = useDoorCrewCard();

  const cData = canvassers.find((d) => d.id == canvasserId);
  const abilities = cData?.abilities ?? [];
  const driverAbility = abilities.find((a) => a.name === 'Driver');
  const canBeDriver = !!driverAbility;
  const nonDriverRoles = abilities.filter((a) => a.name !== 'Driver');

  const [isDriver, setIsDriver] = useState(() => !!(seat.roles ?? []).find((d) => d.name === 'Driver'));
  const [roleValue, setRoleValue] = useState<string>(() => {
    const nonDriver = (seat.roles ?? []).find((d) => d.name !== 'Driver');
    return nonDriver?.id != null ? String(nonDriver.id) : NONE_ROLE_VALUE;
  });
  const [editingComments, setEditingComments] = useState(false);
  const [commentsValue, setCommentsValue] = useState(seat.comments ?? '');

  const commentsRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const initialIsDriver = !!(seat.roles ?? []).find((d) => d.name === 'Driver');
    const initialRole = (seat.roles ?? []).find((d) => d.name !== 'Driver');
    setIsDriver(initialIsDriver);
    setRoleValue(initialRole?.id != null ? String(initialRole.id) : NONE_ROLE_VALUE);
    setCommentsValue(seat.comments ?? '');
  }, [seat.canvasser, seat.id, seat.roles, seat.comments]);

  const handleDriverChange = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!driverAbility) {
      return;
    }
    setIsDriver(checked);
    onUpdateSeat(seat.id, {
      roles: [{ id: driverAbility.id, delete: checked ? 0 : 1 }],
    });
  };

  const handleRoleChange = (event: SelectChangeEvent<string>) => {
    const nextValue = event.target.value;
    const nextId = nextValue ? Number(nextValue) : null;
    const prevId = roleValue ? Number(roleValue) : null;

    if (nextId === prevId) {
      return;
    }

    const roles: SeatRole[] = [];
    if (nextId) {
      roles.push({ id: nextId });
    }
    if (prevId) {
      roles.push({ id: prevId, delete: true });
    }
    if (roles.length) {
      onUpdateSeat(seat.id, { roles });
    }
    setRoleValue(nextValue);
  };

  const handleCommentsBlur = () => {
    setEditingComments(false);
    onUpdateSeat(seat.id, { comments: commentsValue || null });
  };

  const displayName = cData
    ? `${cData.firstname} ${cData.lastname}`
    : `Canvasser #${canvasserId} — not found`;

  return (
    <div className="canvasser-line door-seat-fields">
      <div className="line-item extra-long">
        <span className="minor-title">Name</span>
        <span className={`edit-toggle${cData ? ' no-click' : ''}`} style={cData ? undefined : { fontStyle: 'italic', opacity: 0.7 }}>
          {displayName}
          {cData?.metrics && <span className="slashline">{cData.metrics}</span>}
        </span>
      </div>

      <div className="line-item seat-role-control">
        <span className="minor-title">Driver</span>
        {canBeDriver ? (
          <FormControlLabel
            sx={{ m: 0, alignItems: 'center' }}
            control={
              <Switch
                size="small"
                checked={isDriver}
                onChange={handleDriverChange}
                inputProps={{ 'aria-label': 'Driver' }}
              />
            }
            label={isDriver ? 'Yes' : 'No'}
          />
        ) : (
          <span className="edit-toggle no-click seat-na">n/a</span>
        )}
      </div>

      <div className="line-item long seat-role-control">
        <span className="minor-title">Role</span>
        <FormControl size="small" fullWidth sx={compactControlSx}>
          <InputLabel id={`seat-role-label-${seat.id}`}>Role</InputLabel>
          <Select
            labelId={`seat-role-label-${seat.id}`}
            label="Role"
            value={roleValue}
            onChange={handleRoleChange}
            disabled={!nonDriverRoles.length}
          >
            <MenuItem value={NONE_ROLE_VALUE}>
              <em>None</em>
            </MenuItem>
            {nonDriverRoles.map((ability) => (
              <MenuItem key={ability.id} value={String(ability.id)}>
                {ability.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className="line-item long">
        <span className="minor-title">Comments</span>
        {!editingComments ? (
          <span
            className="edit-toggle"
            onDoubleClick={() => {
              setEditingComments(true);
              setTimeout(() => commentsRef.current?.focus(), 0);
            }}
            title="Double-click to edit"
          >
            {commentsValue || <span className="seat-empty-value">None</span>}
          </span>
        ) : (
          <TextField
            inputRef={commentsRef}
            size="small"
            value={commentsValue}
            onChange={(e) => setCommentsValue(e.target.value)}
            onBlur={handleCommentsBlur}
            autoFocus
            variant="standard"
            sx={{ fontSize: '13px' }}
          />
        )}
      </div>

      {!!seat.dropoff_lat && !!seat.dropoff_lon && (
        <div className="line-item">
          <span className="minor-title">Dropoff</span>
          <DropoffAssignedChip seat={seat} />
        </div>
      )}

      <SeatActions seatId={seat.id} onDeleteSeat={onDeleteSeat} showUnassign />
    </div>
  );
}
